import { SupabaseClient } from '@supabase/supabase-js';
import { UserProfile, UserProfileForm, UserRole } from '@grcs/types/UserRole';
import { hashPassword, parseDOB } from './password';
import { validateStudentInput } from '../users/studentInput';
import { staffPasswordError } from './staffPasswordPolicy';

export type StaffPasswordAction = 'reset-birthday' | 'set-password';

/** Staff credentials live in Auth; application profiles only identify and authorize users. */
export class AccountService {
  constructor(private readonly client: SupabaseClient) {}

  private async requireActiveStaff() {
    const { data: identity, error: identityError } =
      await this.client.auth.getUser();
    if (identityError || !identity.user) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    const { data: profile, error: profileError } = await this.client
      .from('user')
      .select('role, deleted')
      .eq('auth_uid', identity.user.id)
      .single();
    if (
      profileError ||
      !profile ||
      profile.deleted !== false ||
      ![UserRole.TEACHER, UserRole.ADMIN].includes(profile.role)
    ) {
      throw new Error(
        'An active, uniquely linked staff profile is required. Contact an administrator.',
      );
    }
  }

  async changeStaffPassword(password: string) {
    const validation = staffPasswordError(password);
    if (validation) throw new Error(validation);
    // Resolve the current identity from Auth rather than trusting local-storage profile data.
    await this.requireActiveStaff();
    const { error } = await this.client.auth.updateUser({ password });
    if (error) throw error;
  }

  async resetStaffPassword(
    userToken: string,
    action: StaffPasswordAction,
    password?: string,
  ) {
    if (action === 'set-password') {
      const validation = staffPasswordError(password || '');
      if (validation) throw new Error(validation);
    }
    // The server resolves auth_uid and DOB from userToken and authorizes the administrator.
    // Birthday resets deliberately send no password or DOB supplied by the browser.
    await this.invokeManagement({
      userToken,
      action,
      ...(action === 'set-password' ? { password } : {}),
    });
  }

  /** Re-read the target so list snapshots cannot supply stale roles or birthdays. */
  async resetDefaultPassword(userToken: string) {
    const { data: profile, error } = await this.client
      .from('user')
      .select('role, auth_uid, dob')
      .eq('user_token', userToken)
      .eq('deleted', false)
      .single();
    if (error || !profile)
      throw new Error(
        'Could not load the active account. Refresh and try again.',
      );
    if ([UserRole.TEACHER, UserRole.ADMIN].includes(profile.role)) {
      if (!profile.auth_uid)
        throw new Error(
          'Repair this account’s authentication link before resetting its password.',
        );
      return this.resetStaffPassword(userToken, 'reset-birthday');
    }
    if (profile.role !== UserRole.STUDENT || profile.auth_uid) {
      throw new Error(
        'This account does not support a default password reset.',
      );
    }
    const { data, errors } = validateStudentInput({
      first_name: '',
      last_name: '',
      email: '',
      dob: profile.dob,
    });
    if (errors.dob)
      throw new Error(
        'Save a valid date of birth before resetting the password.',
      );
    const password = await hashPassword(parseDOB(data.dob));
    if (!password) throw new Error('The student password could not be reset.');
    // Update only the credential; enforce the legacy student path again at write time.
    const update = this.client
      .from('user')
      .update({ password })
      .eq('user_token', userToken)
      .eq('role', UserRole.STUDENT)
      .eq('deleted', false)
      .eq('dob', profile.dob);
    const guarded =
      profile.auth_uid == null
        ? update.is('auth_uid', null)
        : update.eq('auth_uid', '');
    const result = await guarded.select('user_token').single();
    if (result.error || !result.data)
      throw new Error(
        'The student password reset could not be confirmed. Refresh and try again.',
      );
  }

  /** The server atomically provisions Auth and its linked teacher profile. */
  async createTeacher(
    teacher: UserProfileForm & { password?: string },
    requestId: string,
  ): Promise<UserProfile> {
    const data = await this.invokeManagement({
      action: 'create-teacher',
      teacher,
      requestId,
    });
    if (
      !data.profile?.auth_uid ||
      !data.profile?.user_token ||
      data.profile.role !== UserRole.TEACHER
    ) {
      throw new Error(
        'Teacher creation could not be confirmed. Retry without changing the form.',
      );
    }
    return data.profile;
  }

  private async invokeManagement(body: object) {
    const { data, error } = await this.client.functions.invoke(
      'staff-password',
      { body },
    );
    if (error) {
      let message =
        'Account management failed. Please try again or contact support.';
      try {
        const details = await error.context?.json();
        if (typeof details?.error === 'string') message = details.error;
      } catch {
        // Network errors do not have a JSON response.
      }
      throw new Error(message);
    }
    if (!data?.success)
      throw new Error(data?.error || 'Account management failed.');
    return data;
  }
}
